import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetBankAddressStep, useSubmitBankAddressStep } from '../api';
import type {
  BankAddressField,
  BankAddressFlowHistoryItem,
  BankAddressStepRequestParam,
  BankAddressStepResponse,
  BankAddressStepValues,
} from './types';
import { useAssetsIcons } from '@/shared/api';
import {
  getParamValue,
  mapBankAddressStepToFields,
} from './mapBankAddressStepToFields';

type Props = {
  initialFields?: BankAddressStepResponse;
};

export function useBankAddressStepsFlow({ initialFields }: Props) {
  const [history, setHistory] = useState<BankAddressFlowHistoryItem[]>([]);
  const [step, setStep] = useState<BankAddressStepResponse | null>(null);

  const sentParamsRef = useRef<Set<string>>(new Set());

  const {
    data,
    isLoading: isGetStepLoading,
    error: getStepError,
  } = useGetBankAddressStep({
    enabled: step === null,
  });

  const source = initialFields ? initialFields : data;

  const { assetsIconsData, isAssetsIconsLoading, assetsIconsError } =
    useAssetsIcons();

  const fields: BankAddressField[] = useMemo(
    () =>
      mapBankAddressStepToFields({
        step,
        history,
        assetsIcons: assetsIconsData?.value,
      }),
    [step, history, assetsIconsData]
  );

  const { mutateAsync: submitBankAddressStep, isPending } =
    useSubmitBankAddressStep();

  useEffect(() => {
    if (source && step === null) {
      setStep(source);
    }
  }, [source, step]);

  const submitStep = useCallback(
    async (values: BankAddressStepValues) => {
      if (!step || !values) return;

      const requestParams: BankAddressStepRequestParam[] = fields
        .map(field => {
          const value = values[field.id];
          if (typeof value !== 'string' || value.trim() === '') return null;

          return {
            paramId: field.id,
            paramValue: value,
            isNew: !sentParamsRef.current.has(field.id),
          };
        })
        .filter(Boolean) as BankAddressStepRequestParam[];

      if (requestParams.length === 0) return;

      try {
        const response = await submitBankAddressStep({ params: requestParams });

        setHistory(prev => prev.concat({ fields: step, values }));

        requestParams.forEach(p => sentParamsRef.current.add(p.paramId));

        setStep(response);

        const values = response.params.reduce<BankAddressStepValues>(
          (acc, param) => {
            acc[param.paramId] = getParamValue(param);
            return acc;
          },
          {}
        );

        return values;
      } catch (error) {
        console.error('Failed to submit bank address step:', error);
        return;
      }
    },
    [fields, step, submitBankAddressStep]
  );

  const goBack = useCallback(() => {
    if (history.length === 0) return;

    const last = history[history.length - 1];
    const previousStep = last.fields ?? null;

    setStep(previousStep);
    setHistory(prev => prev.slice(0, prev.length - 1));

    return last.values;
  }, [history]);

  return {
    isFirstStep: history.length === 0,
    isLastStep: Boolean(step?.isDisplayAmount),
    step,
    fields,
    isLoading: isGetStepLoading || isAssetsIconsLoading,
    isPending,
    error: getStepError || assetsIconsError,
    submitStep,
    goBack,
  };
}
